import axiosInstance from './axiosConfig';

export enum TransactionStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  /** @deprecated Display-only. Stored as status=confirmed + accounting_status.linked (so_id). */
  VERIFIED = 'verified',
  FAILED = 'failed',
  CANCELLED = 'cancelled'
}

/**
 * UI status label. "verified" means SO-linked (accounting_status.linked / so_id),
 * not a stored payment status.
 */
export function displayTransactionStatus(txn: {
  status?: string | null;
  so_id?: string | null;
  accounting_status?: { linked?: boolean } | null;
}): string {
  const status = String(txn.status || '').trim().toLowerCase();
  if (
    status === TransactionStatus.FAILED ||
    status === TransactionStatus.CANCELLED ||
    status === TransactionStatus.PENDING
  ) {
    return status;
  }
  // Legacy rows until migrated, or confirmed with SO link
  if (status === TransactionStatus.VERIFIED) return TransactionStatus.VERIFIED;
  const linked =
    txn.accounting_status?.linked === true ||
    !!(txn.so_id && String(txn.so_id).trim());
  if (linked) return TransactionStatus.VERIFIED;
  return status || TransactionStatus.CONFIRMED;
}

export enum TransactionMethod {
  INTERAC_EMAIL = 'interac_email',
  CASH = 'cash',
  ONLINE_CARD = 'online_card',
  TERMINAL_DEBIT = 'terminal_debit',
  TERMINAL_CREDIT = 'terminal_credit',
  DEBIT_CARD = 'debit_card',
  CREDIT_CARD = 'credit_card',
  BANK_TRANSFER = 'bank_transfer',
  CHEQUE = 'cheque',
  THIRD_PARTY = 'third_party',
  PROCESSOR_WITHHOLDING = 'processor_withholding', // Fee withheld by payment processor - ledger only
  RAZORPAY_UPI = 'razorpay_upi',
  RAZORPAY_CARD = 'razorpay_card',
  RAZORPAY_NETBANKING = 'razorpay_netbanking',
  RAZORPAY_WALLET = 'razorpay_wallet',
  UPI = 'upi'
}

export enum TransactionType {
  CREDIT = 'credit',
  DEBIT = 'debit'
}

export interface Transaction {
  _id: string;
  transaction_id: string;
  client_id: string;
  lead_id?: string;
  so_id?: string;
  transaction_name?: string;
  transaction_description?: string;
  transaction_amount: number;
  transaction_currency: string;
  transaction_date: string;
  status: TransactionStatus;
  transaction_method: TransactionMethod;
  sender_name?: string;
  transaction_type: TransactionType;
  is_active: boolean;
  verified_at?: string;
  collected_by?: string;
  paid_by_type?: 'user' | 'company' | 'text' | null;
  paid_by_id?: string | null;
  paid_by_text?: string | null;
  cancelled_by?: string;
  cancellation_reason?: string;
  notes?: string;
  method_details?: any;
  expense_category?: string; // DEPRECATED: Use sub_category instead
  sub_category?: string;
  main_category?: string;
  receipt_url?: string;
  receipt_documents?: Array<{ url: string; filename?: string }>;
  /**
   * Support-document block (mirrors reconciliation_details).
   * `detail` = array of typed ID links (invoice / receipt / document / voucher / payslip).
   */
  documentation_details?: {
    not_required?: boolean;
    exemption_reason?:
      | 'own_account_transfer'
      | 'bank_fee_interest'
      | 'processor_report'
      | 'contra_of_documented_parent'
      | 'other';
    exemption_note?: string;
    detail?: Array<{
      invoice_id?: string;
      receipt_id?: string;
      document_id?: string;
      voucher_id?: string;
      payslip_id?: string;
    }>;
  };
  financial_year?: number; // Financial year (e.g., 2024, 2025)
  // Bookkeeping fields for CPA reporting
  linked_transaction_id?: string;
  payee_id?: string; // Link to Payee
  account_id?: string; // Financial account (AC01, …)
  entity_id?: string; // Legal entity (ET01, …)
  // Reconciliation fields
  reconciled_amount?: number; // For partial matching
  // Payment allocation fields
  has_allocations?: boolean;
  fully_allocated?: boolean;
  // Reconciliation details
  reconciliation_details?: {
    reconciled_status: boolean;
    reconciled_at: string;
    reconciled_amount: number;
    /** Bank line this TXN is reconciled to (BTX…) */
    bank_txn_id?: string;
    /** Credit card line this TXN is reconciled to (CTX…) */
    credit_card_txn_id?: string;
  };
  // Accounting status - tracks internal bookkeeping progress (separate from bank/payment status)
  accounting_status?: {
    categorized: boolean;    // main/sub category set
    documented: boolean;     // has support docs OR documentation_details.not_required
    linked: boolean;         // linked to SO/orders (so_id set OR has_allocations)
    reconciled: boolean;     // matched to bank transaction
    journaled: boolean;      // posted journal exists for this txn
    journal_draft?: boolean; // suggest-journal saved as draft
    completed: boolean;      // all category-required steps done
  };
  /** Origin: manual, email_import, receipts, bank_statement, stripe, razorpay */
  txn_source?: string;
  /** When true, transaction appears in Party Statements */
  statement_register?: boolean;
  /** Manual attention flag (red band). Independent of Party Statements. */
  txn_flagged?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TransactionStats {
  stats: Array<{
    _id: string;
    count: number;
    totalAmount: number;
  }>;
  totalTransactions: number;
  totalAmount: number;
}

export interface TransactionFilters {
  status?: TransactionStatus;
  /** Match any of these statuses (server-side). Takes precedence over `status` when both set. */
  status_in?: TransactionStatus[];
  transaction_method?: TransactionMethod;
  transaction_type?: TransactionType;
  expense_category?: string;
  main_category?: string;
  sub_category?: string;
  amount?: number;
  startDate?: string;
  endDate?: string;
  financial_year?: number;
  /** Ledger book id (e.g. AC02) — filters transactions tagged to this book */
  account_id?: string;
  /** Multiple ledger books (company "all accounts" scope) */
  account_ids?: string[];
  /** Legal entity (ET01, …) */
  entity_id?: string;
  /** Resolve all linked accounts for a company (optional account_id narrows within it) */
  companyId?: string;
  client_id?: string;
  lead_id?: string;
  so_id?: string;
  serviceType?: string;
  serviceName?: string;
  page?: number;
  limit?: number;
  search?: string;
  // Accounting status filters
  accounting_categorized?: boolean;
  accounting_documented?: boolean;
  accounting_journaled?: boolean;
  accounting_journal_draft?: boolean;
  accounting_linked?: boolean;
  accounting_reconciled?: boolean;
  accounting_completed?: boolean;
  // Negative accounting status filters (for filtering items that are NOT in these states)
  accounting_not_categorized?: boolean;
  accounting_not_documented?: boolean;
  accounting_not_journaled?: boolean;
  accounting_not_linked?: boolean;
  accounting_not_reconciled?: boolean;
  /** Party Statements flag: true = on register only, false = not on register */
  statement_register?: boolean;
  /** Manual attention flag: true = flagged only, false = not flagged */
  txn_flagged?: boolean;
  /** Staff user id — matches collected_by or method_details.collected_by */
  collected_by?: string;
  /** Origin: manual, email_import, receipts, bank_statement, stripe, razorpay */
  txn_source?: string;
  /** Reuse a total the page already has. The server skips countDocuments. */
  skipCount?: boolean;
  /** Ask only for the matching total. */
  countOnly?: boolean;
  /** Last row of the previous page. The server does not skip. */
  afterDate?: string;
  afterId?: string;
}

export interface OperatingRevenueSummary {
  total_operating_revenue: number;
  estimated_gst: number;
  hst_rate: number;
}

export interface PaginatedResponse<T> {
  transactions: T[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number | null;
    totalPages: number | null;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  operating_revenue_summary?: OperatingRevenueSummary;
}

// Get all transactions with pagination
export function shouldSkipTransactionCount(
  page: number,
  filterKey: string,
  previousKey: string,
  knownTotal: number
): boolean {
  return page > 1 && filterKey === previousKey && knownTotal > 0;
}

export const getTransactions = async (filters?: TransactionFilters): Promise<PaginatedResponse<Transaction>> => {
  const params = new URLSearchParams();

  if (filters?.status_in?.length) {
    params.append('status_in', filters.status_in.join(','));
  } else if (filters?.status) {
    params.append('status', filters.status);
  }
  if (filters?.transaction_method) params.append('transaction_method', filters.transaction_method);
  if (filters?.transaction_type) params.append('transaction_type', filters.transaction_type);
  if (filters?.expense_category) params.append('expense_category', filters.expense_category);
  if (filters?.main_category) params.append('main_category', filters.main_category);
  if (filters?.sub_category) params.append('sub_category', filters.sub_category);
  if (filters?.amount !== undefined) params.append('amount', filters.amount.toString());
  if (filters?.startDate) params.append('startDate', filters.startDate);
  if (filters?.endDate) params.append('endDate', filters.endDate);
  if (filters?.financial_year) params.append('financial_year', filters.financial_year.toString());
  if (filters?.companyId) params.append('companyId', filters.companyId);
  if (filters?.entity_id) params.append('entity_id', filters.entity_id);
  if (filters?.account_ids?.length) {
    params.append('account_ids', filters.account_ids.join(','));
  } else if (filters?.account_id) {
    params.append('account_id', filters.account_id);
  }
  if (filters?.client_id) params.append('client_id', filters.client_id);
  if (filters?.lead_id) params.append('lead_id', filters.lead_id);
  if (filters?.so_id) params.append('so_id', filters.so_id);
  if (filters?.serviceType) params.append('serviceType', filters.serviceType);
  if (filters?.serviceName) params.append('serviceName', filters.serviceName);
  if (filters?.page) params.append('page', filters.page.toString());
  if (filters?.limit) params.append('limit', filters.limit.toString());
  if (filters?.search) params.append('search', filters.search);
  if (filters?.accounting_categorized !== undefined) {
    params.append('accounting_categorized', filters.accounting_categorized.toString());
  }
  if (filters?.accounting_documented !== undefined) {
    params.append('accounting_documented', filters.accounting_documented.toString());
  }
  if (filters?.accounting_journaled !== undefined) {
    params.append('accounting_journaled', filters.accounting_journaled.toString());
  }
  if (filters?.accounting_journal_draft !== undefined) {
    params.append('accounting_journal_draft', filters.accounting_journal_draft.toString());
  }
  if (filters?.accounting_linked !== undefined) {
    params.append('accounting_linked', filters.accounting_linked.toString());
  }
  if (filters?.accounting_reconciled !== undefined) {
    params.append('accounting_reconciled', filters.accounting_reconciled.toString());
  }
  if (filters?.accounting_completed !== undefined) {
    params.append('accounting_completed', filters.accounting_completed.toString());
  }
  if (filters?.accounting_not_categorized !== undefined) {
    params.append('accounting_not_categorized', filters.accounting_not_categorized.toString());
  }
  if (filters?.accounting_not_documented !== undefined) {
    params.append('accounting_not_documented', filters.accounting_not_documented.toString());
  }
  if (filters?.accounting_not_journaled !== undefined) {
    params.append('accounting_not_journaled', filters.accounting_not_journaled.toString());
  }
  if (filters?.accounting_not_linked !== undefined) {
    params.append('accounting_not_linked', filters.accounting_not_linked.toString());
  }
  if (filters?.accounting_not_reconciled !== undefined) {
    params.append('accounting_not_reconciled', filters.accounting_not_reconciled.toString());
  }
  if (filters?.statement_register !== undefined) {
    params.append('statement_register', filters.statement_register.toString());
  }
  if (filters?.txn_flagged !== undefined) {
    params.append('txn_flagged', filters.txn_flagged.toString());
  }
  if (filters?.collected_by) {
    params.append('collected_by', filters.collected_by);
  }
  if (filters?.txn_source) {
    params.append('txn_source', filters.txn_source);
  }
  if (filters?.skipCount) {
    params.append('skip_count', 'true');
  }
  if (filters?.countOnly) {
    params.append('count_only', 'true');
  }
  if (filters?.afterDate && filters?.afterId) {
    params.append('after_date', filters.afterDate);
    params.append('after_id', filters.afterId);
  }

  const url = `/transactions?${params.toString()}`;
  const response = await axiosInstance.get(url);
  return response.data;
};

export const getTransactionCount = async (filters?: TransactionFilters): Promise<number> => {
  const response = await getTransactions({ ...filters, countOnly: true, skipCount: false, page: 1 });
  return response.pagination?.totalCount ?? 0;
};

// Get all transactions without pagination (for combined pagination)
// Fetches in batches to avoid timeouts
export const getAllTransactions = async (filters?: Omit<TransactionFilters, 'page' | 'limit'>): Promise<Transaction[]> => {
  const allTransactions: Transaction[] = [];
  let page = 1;
  const pageSize = 5000; // Efficient batch size matching backend limit (5000) to minimize network roundtrips
  let hasMore = true;

  while (hasMore) {
    const params = new URLSearchParams();
    
    if (filters?.status_in?.length) {
      params.append('status_in', filters.status_in.join(','));
    } else if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.transaction_method) params.append('transaction_method', filters.transaction_method);
    if (filters?.transaction_type) params.append('transaction_type', filters.transaction_type);
    if (filters?.expense_category) params.append('expense_category', filters.expense_category);
    if (filters?.main_category) params.append('main_category', filters.main_category);
    if (filters?.sub_category) params.append('sub_category', filters.sub_category);
    if (filters?.amount !== undefined) params.append('amount', filters.amount.toString());
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.financial_year) {
      params.append('financial_year', filters.financial_year.toString());
    }
    if (filters?.companyId) params.append('companyId', filters.companyId);
    if (filters?.entity_id) params.append('entity_id', filters.entity_id);
    if (filters?.account_ids?.length) {
      params.append('account_ids', filters.account_ids.join(','));
    } else if (filters?.account_id) {
      params.append('account_id', filters.account_id);
    }
    if (filters?.client_id) params.append('client_id', filters.client_id);
    if (filters?.lead_id) params.append('lead_id', filters.lead_id);
    if (filters?.so_id) params.append('so_id', filters.so_id);
    if (filters?.serviceType) params.append('serviceType', filters.serviceType);
    if (filters?.serviceName) params.append('serviceName', filters.serviceName);
    if (filters?.search) params.append('search', filters.search);
    // Accounting status filters
    if (filters?.accounting_categorized !== undefined) params.append('accounting_categorized', filters.accounting_categorized.toString());
    if (filters?.accounting_documented !== undefined) params.append('accounting_documented', filters.accounting_documented.toString());
    if (filters?.accounting_journaled !== undefined) params.append('accounting_journaled', filters.accounting_journaled.toString());
    if (filters?.accounting_journal_draft !== undefined) params.append('accounting_journal_draft', filters.accounting_journal_draft.toString());
    if (filters?.accounting_linked !== undefined) params.append('accounting_linked', filters.accounting_linked.toString());
    if (filters?.accounting_reconciled !== undefined) params.append('accounting_reconciled', filters.accounting_reconciled.toString());
    if (filters?.accounting_completed !== undefined) params.append('accounting_completed', filters.accounting_completed.toString());
    // Negative accounting status filters
    if (filters?.accounting_not_categorized !== undefined) params.append('accounting_not_categorized', filters.accounting_not_categorized.toString());
    if (filters?.accounting_not_documented !== undefined) params.append('accounting_not_documented', filters.accounting_not_documented.toString());
    if (filters?.accounting_not_journaled !== undefined) params.append('accounting_not_journaled', filters.accounting_not_journaled.toString());
    if (filters?.accounting_not_linked !== undefined) params.append('accounting_not_linked', filters.accounting_not_linked.toString());
    if (filters?.accounting_not_reconciled !== undefined) params.append('accounting_not_reconciled', filters.accounting_not_reconciled.toString());
    if (filters?.statement_register !== undefined) {
      params.append('statement_register', filters.statement_register.toString());
    }
    if (filters?.txn_flagged !== undefined) {
      params.append('txn_flagged', filters.txn_flagged.toString());
    }
    if (filters?.collected_by) {
      params.append('collected_by', filters.collected_by);
    }
    if (filters?.txn_source) {
      params.append('txn_source', filters.txn_source);
    }

    params.append('page', page.toString());
    params.append('limit', pageSize.toString());

    try {
      const response = await axiosInstance.get(`/transactions?${params.toString()}`);
      
      // Handle both direct array response and paginated response object
      let batch: Transaction[] = [];
      let totalPages = 1;
      
      if (Array.isArray(response.data)) {
        batch = response.data;
        hasMore = batch.length === pageSize;
      } else if (response.data && Array.isArray(response.data.transactions)) {
        batch = response.data.transactions;
        totalPages = response.data.pagination?.totalPages || 1;
        hasMore = page < totalPages;
      } else if (response.data && response.data.data && Array.isArray(response.data.data.transactions)) {
        batch = response.data.data.transactions;
        totalPages = response.data.data.pagination?.totalPages || 1;
        hasMore = page < totalPages;
      }
      
      allTransactions.push(...batch);
      
      // Stop if we got fewer records than requested (last page) or no more pages
      if (batch.length < pageSize || !hasMore) {
        hasMore = false;
      } else {
        page++;
      }
    } catch (error) {
      console.error(`Error fetching transactions page ${page}:`, error);
      hasMore = false; // Stop on error
    }
  }
  
  return allTransactions;
};

export type TransactionRowLabels = {
  clients: Array<{ clientId: string; name: string }>;
  services: Array<{ soId: string; name: string; country?: string }>;
};

export const getTransactionRowLabels = async (input: {
  clientIds: string[];
  soIds: string[];
}): Promise<TransactionRowLabels> => {
  const response = await axiosInstance.post<TransactionRowLabels>('/transactions/row-labels', input);
  return {
    clients: response.data?.clients || [],
    services: response.data?.services || [],
  };
};

// Get transaction by ID
export const getTransactionById = async (transactionId: string): Promise<Transaction> => {
  const response = await axiosInstance.get(`/transactions/${transactionId}`);
  return response.data;
};

// Get transactions by client
export const getTransactionsByClient = async (clientId: string): Promise<Transaction[]> => {
  const response = await axiosInstance.get(`/transactions/client/${clientId}`);
  return response.data;
};

// Get transactions by service order
export const getTransactionsByServiceOrder = async (soId: string): Promise<Transaction[]> => {
  const response = await axiosInstance.get(`/transactions/service-order/${soId}`);
  return response.data;
};

// Get transactions by lead
export const getTransactionsByLead = async (leadId: string): Promise<Transaction[]> => {
  const response = await axiosInstance.get(`/transactions/lead/${leadId}`);
  return response.data;
};

// Update transaction status
export const updateTransactionStatus = async (
  transactionId: string, 
  updates: {
    status?: TransactionStatus;
    verified_by?: string;
    notes?: string;
    transaction_name?: string;
    transaction_description?: string;
    client_id?: string;
    client_name?: string;
    lead_id?: string;
    so_id?: string;
    expense_category?: string; // DEPRECATED: Use sub_category instead
    sub_category?: string;
    main_category?: string;
    transaction_date?: string;
    receipt_url?: string;
    receipt_status?: 'missing' | 'pending' | 'received';
    tax_details?: {
      tax_included?: boolean;
      gst_amount?: number;
      hst_amount?: number;
      pst_amount?: number;
      tax_rate?: number;
      tax_notes?: string;
    };
    method_details?: any;
    payee_id?: string; // Link to Payee
    collected_by?: string | null;
    transaction_method?: TransactionMethod;
    paid_by_type?: 'user' | 'company' | 'text' | null;
    paid_by_id?: string | null;
    paid_by_text?: string | null;
    account_id?: string; // Ledger book reassignment
    statement_register?: boolean; // Include in Party Statements
    txn_flagged?: boolean; // Manual attention flag (red band)
    documentation_details?: {
      not_required?: boolean;
      exemption_reason?: string | null;
      exemption_note?: string | null;
      detail?: Array<{
        invoice_id?: string;
        receipt_id?: string;
        document_id?: string;
        voucher_id?: string;
        payslip_id?: string;
      }>;
    };
    documentation_not_required?: boolean;
    documentation_exemption_reason?: string | null;
    documentation_exemption_note?: string | null;
    // DEPRECATED: Kept for backward compatibility only
    contractor_name?: string; // For contractor payments (T4A)
    contractor_tax_id?: string;
  }
): Promise<Transaction> => {
  const response = await axiosInstance.patch(`/transactions/${transactionId}`, updates);
  return response.data?.transaction || response.data;
};

// Alias for backward compatibility and clearer naming
export const updateTransaction = updateTransactionStatus;

// Get transaction statistics
export const getTransactionStats = async (
  startDate?: string, 
  endDate?: string,
  transaction_method?: TransactionMethod,
  status?: TransactionStatus
): Promise<TransactionStats> => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (transaction_method) params.append('transaction_method', transaction_method);
  if (status) params.append('status', status);

  const response = await axiosInstance.get(`/transactions/stats?${params.toString()}`);
  return response.data;
};

// Create cash transaction
export const createCashTransaction = async (transactionData: {
  client_id: string;
  lead_id?: string;
  so_id?: string;
  account_id?: string;
  transaction_name?: string;
  transaction_description?: string;
  transaction_amount: number;
  transaction_currency?: string;
  transaction_date: string;
  sender_name?: string;
  receipt_number?: string;
  collected_by?: string;
  /** Manual collection method (cash, upi, bank_transfer, cheque, debit_card, credit_card); defaults to cash. */
  transaction_method?: TransactionMethod;
}): Promise<Transaction> => {
  const response = await axiosInstance.post('/transactions/cash', transactionData);
  return response.data;
};

// Create Interac email transaction
export const createInteracEmailTransaction = async (transactionData: {
  client_id: string;
  lead_id?: string;
  so_id?: string;
  transaction_name?: string;
  transaction_description?: string;
  transaction_amount: number;
  transaction_currency?: string;
  transaction_date: string;
  sender_name?: string;
  reference_number?: string;
  message?: string;
  email_subject?: string;
  sender_email?: string;
  email_content?: string;
}): Promise<Transaction> => {
  const response = await axiosInstance.post('/transactions/interac-email', transactionData);
  return response.data;
};

// Create debit transaction (outflow/expense)
export const createDebitTransaction = async (transactionData: {
  transaction_name?: string;
  transaction_description?: string;
  transaction_amount: number;
  transaction_currency?: string;
  transaction_date: string;
  transaction_method: TransactionMethod;
  expense_category?: string;
  receipt_url?: string;
  notes?: string;
  paid_to?: string;
  hst_amount?: number;
  hst_rate?: number;
  linked_transaction_id?: string;
  payee_id?: string; // Link to Payee
  account_id?: string;
  /** Staff user id — who handled the payout (Paid by) */
  collected_by?: string;
  paid_by_type?: 'user' | 'company' | 'text';
  paid_by_id?: string;
  paid_by_text?: string;
  txn_source?: string;
}): Promise<Transaction> => {
  const response = await axiosInstance.post('/transactions/debit', transactionData);
  return response.data?.transaction || response.data;
};

const STRIPE_TRANSACTION_METHODS = new Set<string>([
  TransactionMethod.ONLINE_CARD,
  TransactionMethod.TERMINAL_CREDIT,
  TransactionMethod.TERMINAL_DEBIT,
]);

export function isStripeTransactionMethod(method?: TransactionMethod | string): boolean {
  const key = String(method || '').toLowerCase().trim();
  if (STRIPE_TRANSACTION_METHODS.has(key)) return true;
  return key.startsWith('stripe_');
}

type CancelEligibilityInput =
  | Pick<Transaction, 'status' | 'transaction_method'>
  | TransactionStatus
  | string;

/** Whether admin can cancel from the UI (cash/interac duplicates OK; not settled Stripe). */
export function canCancelTransaction(txn?: CancelEligibilityInput | null): boolean {
  if (!txn) return false;

  let status: string;
  let method: string | undefined;

  if (typeof txn === 'object') {
    status = String(txn.status || '');
    method = txn.transaction_method;
  } else {
    status = String(txn);
  }

  const statusKey = status.toLowerCase().trim();
  if (statusKey === TransactionStatus.CANCELLED || statusKey === 'cancelled') return false;

  if (isStripeTransactionMethod(method)) {
    if (statusKey === TransactionStatus.CONFIRMED || statusKey === 'confirmed') return false;
    if (statusKey === TransactionStatus.VERIFIED || statusKey === 'verified') return false;
  }

  return true;
}

// Cancel transaction
export const cancelTransaction = async (
  transactionId: string,
  cancellationData: {
    cancelled_by?: string;
    cancellation_reason?: string;
    notes?: string;
  }
): Promise<Transaction> => {
  const response = await axiosInstance.post(`/transactions/${transactionId}/cancel`, cancellationData);
  const data = response.data;
  return data?.transaction ?? data;
};

// Get aggregated collected amounts by service order (optimized - single request)
// Returns: { [so_id]: { [currency]: amount } }
export const getCollectedAmountsByServiceOrder = async (): Promise<Record<string, Record<string, number>>> => {
  try {
    const response = await axiosInstance.get<Record<string, Record<string, number>>>('/transactions/collected-by-so');
    return response.data;
  } catch (error) {
    console.error('Error fetching collected amounts by service order:', error);
    return {};
  }
};

/** Flag or unflag a transaction for the Party Statements */
export const setStatementRegister = async (
  transactionId: string,
  statementRegister: boolean
): Promise<Transaction> => {
  return updateTransactionStatus(transactionId, { statement_register: statementRegister });
};

/** Manual attention flag (red band in Revenue/Outflow). Independent of Party Statements. */
export const setTxnFlagged = async (
  transactionId: string,
  txnFlagged: boolean
): Promise<Transaction> => {
  return updateTransactionStatus(transactionId, { txn_flagged: txnFlagged });
};

/** Delete one receipt document by index */
export const deleteReceiptDocument = async (transactionId: string, index: number): Promise<Transaction> => {
  const response = await axiosInstance.delete(`/transactions/${transactionId}/supporting-documents/${index}`);
  return response.data?.transaction || response.data;
};

/** Delete all receipt documents for a transaction */
export const deleteAllReceiptDocuments = async (transactionId: string): Promise<Transaction> => {
  const response = await axiosInstance.delete(`/transactions/${transactionId}/supporting-documents/all`);
  return response.data?.transaction || response.data;
};

/** Link an existing receipt document to another transaction without re-uploading */
export const linkReceiptDocumentToTransaction = async (
  sourceTransactionId: string,
  sourceIndex: number,
  targetTransactionId: string
): Promise<Transaction> => {
  const response = await axiosInstance.post(
    `/transactions/${sourceTransactionId}/supporting-documents/${sourceIndex}/link`,
    { target_transaction_id: targetTransactionId }
  );
  return response.data?.transaction || response.data;
};

/** Normalize receipt documents list from transaction (supports legacy receipt_url) */
export const getReceiptDocuments = (t: Transaction | null): Array<{ url: string; filename?: string }> => {
  if (!t) return [];
  if (t.receipt_documents?.length) return t.receipt_documents;
  if (t.receipt_url?.trim()) return [{ url: t.receipt_url, filename: 'receipt' }];
  return [];
};

/** Fetch receipt document via view proxy (inline) and return a blob URL for in-app display (PDF/images) */
export const getReceiptDocumentViewBlobUrl = async (
  transactionId: string,
  index: number
): Promise<{ url: string; mimeType: string }> => {
  const response = await axiosInstance.get(
    `/transactions/${transactionId}/supporting-documents/${index}/view`,
    { responseType: 'blob' }
  );
  const mimeType = (response.data as Blob).type || response.headers['content-type'] || 'application/octet-stream';
  const url = URL.createObjectURL(response.data as Blob);
  return { url, mimeType };
};
 